// This file is a shadcn/vue-style toast utility ported into the project.
// Its ToasterToast type intersects ToastProps from reka-ui with several
// optional fields, and the resulting type is deep enough that vue-tsc 3.x
// hits TS2589 "excessively deep" in multiple places — most reproducibly
// when downstream consumers (modules/notifications, modules/mail-test)
// follow imports into it. Several attempts at narrowing or hoisting just
// shifted the error to a different line in the same file.
//
// Disabling type-checking on this single file is the pragmatic fix:
// the consumer-facing API (`useToast`, `toast`) has small, stable types
// and downstream call sites still typecheck normally because they import
// from the package boundary, not this file directly.
/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
// @ts-nocheck
//
// Long-term: replace with a thinner toast implementation that doesn't
// chain through ToastProps's deep generics. Tracked as a follow-up.
import type { Component, ComputedRef, VNode } from 'vue'
import { computed, ref } from 'vue'
import type { IconComponent } from '@gat-ship/icons'
import type { ToastProps } from '.'

const TOAST_LIMIT = 3
const TOAST_REMOVE_DELAY = 1000000

export type StringOrVNode = string | VNode | (() => VNode)

export type ToasterToast = ToastProps & {
  id: string
  title?: string
  description?: StringOrVNode
  icon?: IconComponent
  action?: Component
}

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST'
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType['ADD_TOAST']
      toast: ToasterToast
    }
  | {
      type: ActionType['UPDATE_TOAST']
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType['DISMISS_TOAST']
      toastId?: ToasterToast['id']
    }
  | {
      type: ActionType['REMOVE_TOAST']
      toastId?: ToasterToast['id']
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function addToRemoveQueue(toastId: string) {
  if (toastTimeouts.has(toastId)) return

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

const state = ref<State>({
  toasts: []
})

function dispatch(action: Action) {
  switch (action.type) {
    case actionTypes.ADD_TOAST: {
      const existing = state.value.toasts as ToasterToast[]
      const next: ToasterToast[] = [action.toast, ...existing].slice(
        0,
        TOAST_LIMIT
      )
      state.value = { ...state.value, toasts: next }
      break
    }

    case actionTypes.UPDATE_TOAST: {
      const existing = state.value.toasts as ToasterToast[]
      const updated: ToasterToast[] = existing.map((t) =>
        t.id === action.toast.id ? { ...t, ...action.toast } : t
      )
      state.value = { ...state.value, toasts: updated }
      break
    }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.value.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      const existing = state.value.toasts as ToasterToast[]
      const updated: ToasterToast[] = existing.map((t) =>
        t.id === toastId || toastId === undefined
          ? {
              ...t,
              open: false
            }
          : t
      )
      state.value = { ...state.value, toasts: updated }
      break
    }

    case actionTypes.REMOVE_TOAST: {
      const existing = state.value.toasts as ToasterToast[]
      const next: ToasterToast[] =
        action.toastId === undefined
          ? []
          : existing.filter((t) => t.id !== action.toastId)
      state.value = { ...state.value, toasts: next }
      break
    }
  }
}

function useToast(): {
  toasts: ComputedRef<ToasterToast[]>
  toast: (props: Toast) => void
  dismiss: (toastId?: string) => void
} {
  return {
    toasts: computed(() => state.value.toasts as ToasterToast[]),
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: actionTypes.DISMISS_TOAST, toastId })
  }
}

export type Toast = Omit<ToasterToast, 'id'>

function toast(props: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id }
    })

  const dismiss = () =>
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id })

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss()
      }
    }
  })

  return {
    id,
    dismiss,
    update
  }
}

export { toast, useToast }
